import { Link, NavLink } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { NAV_LINKS, SOCIAL_LINKS } from '@/data/contactInfo';

const BRAND_BG = '#E95526';

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    // Hysteresis to prevent flicker: different thresholds for shrink vs. expand.
    // The header height changes by ~40px on shrink, so the gap between thresholds
    // must be larger than that to avoid an infinite compress/expand loop.
    const SHRINK_AT = 80;
    const EXPAND_AT = 20;
    let ticking = false;
    let lastState = window.scrollY > SHRINK_AT;
    setScrolled(lastState);
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        if (!lastState && y > SHRINK_AT) {
          lastState = true;
          setScrolled(true);
        } else if (lastState && y < EXPAND_AT) {
          lastState = false;
          setScrolled(false);
        }
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const closeMobile = () => setOpen(false);

  // Heights: default h-20/h-24 (80/96px). When scrolled: shrink ~40% to h-12/h-14 (48/56px)
  const headerHeightClasses = scrolled ? 'h-12 sm:h-14' : 'h-20 sm:h-24';
  const headerPaddingY = scrolled ? '2px' : '3px';
  // Logo sizes scaled down ~40% when scrolled
  const logoClasses = scrolled
    ? 'h-[22px] sm:h-[26px] lg:h-[30px]'
    : 'h-[34px] sm:h-[40px] lg:h-[48px]';
  const navTextSize = scrolled ? '13px' : '15px';
  const socialIconBox = scrolled ? 'w-5 h-5' : 'w-6 h-6';
  const mobileBtnSize = scrolled ? 'w-9 h-9' : 'w-11 h-11';
  const mobileIconSize = scrolled ? 18 : 22;

  return (
    <header
      className="sticky top-0 z-50 border-b border-black/10 transition-[backdrop-filter] duration-300"
      style={{ backgroundColor: BRAND_BG }}
      data-testid="site-header"
    >
      <div
        className={`container-link flex items-center justify-between transition-[height] duration-300 ease-out ${headerHeightClasses}`}
        style={{ paddingTop: headerPaddingY, paddingBottom: headerPaddingY }}
      >
        <Link
          to="/"
          className="flex items-center group"
          style={{ marginLeft: '10px' }}
          data-testid="header-logo-link"
        >
          <img
            src="/assets/logos/link-horizontal-blanco-menu.png"
            alt="Link Consultores de Mercadeo"
            className={`${logoClasses} w-auto transition-[height] duration-300 ease-out group-hover:scale-105`}
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8" data-testid="site-header-nav">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.href}
              to={l.href}
              data-testid={`header-nav-link-${l.href.replace(/\//g, '')}`}
              style={({ isActive }) => ({
                fontFamily: isActive ? undefined : 'Gotham, sans-serif',
                fontWeight: isActive ? 400 : 500,
                fontSize: isActive
                  ? `${parseInt(navTextSize) + 3}px`
                  : navTextSize,
                transition: 'font-size 300ms ease',
              })}
              className={({ isActive }) =>
                `capitalize tracking-wide transition-colors duration-200 ${
                  isActive
                    ? 'text-white font-wild italic'
                    : 'text-white/85 hover:text-white'
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        {/* Desktop socials - no background */}
        <div className="hidden md:flex items-center gap-4">
          {SOCIAL_LINKS.map((s) => (
            <a
              key={s.name}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={s.name}
              className="inline-flex items-center justify-center transition-transform duration-200 hover:scale-110"
              data-testid={`header-social-${s.name.toLowerCase()}`}
            >
              <img
                src={s.icon}
                alt={s.name}
                className={`${socialIconBox} object-contain transition-all duration-300`}
              />
            </a>
          ))}
        </div>

        {/* Mobile menu button */}
        <button
          className={`md:hidden ${mobileBtnSize} rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-white transition-all duration-300`}
          onClick={() => setOpen((v) => !v)}
          aria-label="Abrir menú"
          data-testid="mobile-menu-open-button"
        >
          {open ? <X size={mobileIconSize} /> : <Menu size={mobileIconSize} />}
        </button>
      </div>

      {/* Mobile sheet */}
      {open && (
        <div
          className="md:hidden border-t border-white/15"
          style={{ backgroundColor: BRAND_BG }}
          data-testid="mobile-menu-sheet"
        >
          <div className="container-link py-6 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.href}
                to={l.href}
                onClick={closeMobile}
                style={{ fontFamily: 'Gotham, sans-serif', fontWeight: 500 }}
                className={({ isActive }) =>
                  `text-lg py-3 capitalize tracking-wide border-b border-white/15 ${
                    isActive ? 'text-white' : 'text-white/90'
                  }`
                }
                data-testid={`mobile-nav-link-${l.href.replace(/\//g, '')}`}
              >
                {l.label}
              </NavLink>
            ))}
            <div className="flex items-center gap-5 pt-6">
              {SOCIAL_LINKS.map((s) => (
                <a
                  key={s.name}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.name}
                  className="inline-flex items-center justify-center transition-transform duration-200 hover:scale-110"
                >
                  <img src={s.icon} alt={s.name} className="w-6 h-6 object-contain" />
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
